import {
  createIndiaDateTimeIso,
  formatIndiaTime,
  getIndiaDateParts,
  getIndiaDateKey,
  getIndiaEndOfDateMs,
  getIndiaTimeInputValue,
  INDIA_UTC_OFFSET_MS,
  INDIA_TIME_ZONE,
} from "./indiaTime";

export const DEFAULT_LUNCH_DEDUCTION_SECONDS = 45 * 60;
export const AUTO_CHECKOUT_TIME_LABEL = "Auto checkout";
export {
  createIndiaDateTimeIso,
  formatIndiaTime,
  getIndiaDateKey,
  getIndiaEndOfDateMs,
  getIndiaTimeInputValue,
  INDIA_TIME_ZONE,
};
const AUTO_CHECKOUT_MINUTE_THRESHOLD = 55;

export const isLegacyIndianClockStoredAsUtc = (record = {}) => false;

export const getAttendanceDisplayTimestamp = (record = {}, field) => {
  const value = record?.[field];
  if (!value || !isLegacyIndianClockStoredAsUtc(record)) return value;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Date(parsed.getTime() - INDIA_UTC_OFFSET_MS).toISOString();
};

export const formatAttendanceRecordTime = (record = {}, field, fallback = "-") =>
  formatIndiaTime(getAttendanceDisplayTimestamp(record, field), fallback);

export const getAttendanceRecordTimeInputValue = (record = {}, field) =>
  getIndiaTimeInputValue(getAttendanceDisplayTimestamp(record, field));

export const formatDurationSeconds = (totalSeconds = 0) => {
  const safeSeconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

export const isAutoCheckoutRecord = (record) => {
  if (!record?.checkOut) return false;
  const value = new Date(record.checkOut);
  if (Number.isNaN(value.getTime())) return false;
  const indiaParts = getIndiaDateParts(value);
  const indiaEndOfDay = indiaParts?.hour === 23 && indiaParts.minute >= AUTO_CHECKOUT_MINUTE_THRESHOLD;
  const legacyUtcEndOfDay = value.getUTCHours() === 23 && value.getUTCMinutes() >= AUTO_CHECKOUT_MINUTE_THRESHOLD;
  return indiaEndOfDay || legacyUtcEndOfDay;
};

export const getAutoCheckoutDisplayTime = (record, formatTime = () => "-") => (
  formatTime(record?.checkOut)
);

export const getAutoCheckoutEndMs = (record) => {
  const indiaEndMs = getIndiaEndOfDateMs(record?.date);
  if (!Number.isNaN(indiaEndMs)) return indiaEndMs;
  const checkoutMs = record?.checkOut ? new Date(record.checkOut).getTime() : NaN;
  return Number.isNaN(checkoutMs) ? Date.now() : checkoutMs;
};

export const overlapSeconds = (startMs, endMs, windowStartMs, windowEndMs) => {
  if ([startMs, endMs, windowStartMs, windowEndMs].some((value) => Number.isNaN(value))) return 0;
  const start = Math.max(startMs, windowStartMs);
  const end = Math.min(endMs, windowEndMs);
  return end > start ? Math.floor((end - start) / 1000) : 0;
};

export const isCheckoutClosedBreak = (endMs, checkOutMs) => (
  !Number.isNaN(endMs) &&
  !Number.isNaN(checkOutMs) &&
  Math.abs(endMs - checkOutMs) < 60000
);

export const getDeductibleBreakSeconds = ({ seconds, type, endMs, checkOutMs }) => {
  const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  if ((type === "lunch" || !type) && isCheckoutClosedBreak(endMs, checkOutMs)) {
    return Math.min(safeSeconds, DEFAULT_LUNCH_DEDUCTION_SECONDS);
  }
  return safeSeconds;
};

export const getBreakDurationSeconds = (record, nowMs = Date.now()) => {
  const checkInMs = record?.checkIn ? new Date(record.checkIn).getTime() : NaN;
  const workEndMs = record?.checkOut ? new Date(record.checkOut).getTime() : nowMs;

  const breaksSeconds = (record?.breaks || []).reduce((total, item) => {
    if (!item?.startTime) return total;

    const startMs = new Date(item.startTime).getTime();
    const endMs = item.endTime ? new Date(item.endTime).getTime() : nowMs;

    return total + getDeductibleBreakSeconds({
      seconds: overlapSeconds(startMs, endMs, checkInMs, workEndMs),
      type: item.type,
      endMs,
      checkOutMs: record?.checkOut ? workEndMs : NaN,
    });
  }, 0);

  if (breaksSeconds > 0) return breaksSeconds;

  if (record?.breakStart && record?.breakEnd) {
    const startMs = new Date(record.breakStart).getTime();
    const endMs = new Date(record.breakEnd).getTime();
    return getDeductibleBreakSeconds({
      seconds: overlapSeconds(startMs, endMs, checkInMs, workEndMs),
      type: "lunch",
      endMs,
      checkOutMs: record?.checkOut ? workEndMs : NaN,
    });
  }

  return 0;
};

export const getWorkDurationSeconds = (record, nowMs = Date.now()) => {
  if (!record?.checkIn) return 0;

  const checkInMs = new Date(record.checkIn).getTime();
  const autoCheckout = isAutoCheckoutRecord(record);
  const endMs = autoCheckout
    ? getAutoCheckoutEndMs(record)
    : record.checkOut ? new Date(record.checkOut).getTime() : nowMs;

  if (!Number.isNaN(checkInMs) && !Number.isNaN(endMs) && endMs > checkInMs) {
    const grossSeconds = Math.floor((endMs - checkInMs) / 1000);
    const durationRecord = autoCheckout
      ? { ...record, checkOut: new Date(endMs).toISOString() }
      : record;
    const breakSeconds = Math.min(grossSeconds, getBreakDurationSeconds(durationRecord, endMs));
    return Math.max(0, grossSeconds - breakSeconds);
  }

  if (record.duration !== undefined) {
    return Number(record.duration || 0) * 60;
  }

  return 0;
};

export const getWorkDurationLabel = (record, fallback = "-") => {
  if (!record?.checkIn || record?.isAbsentPlaceholder) return record?.workHours || fallback;
  return formatDurationSeconds(getWorkDurationSeconds(record));
};
